// useCurrencyRates — fetch + cache de cotizaciones offline-friendly.
// API gratuita sin key: open.er-api.com (base USD)
// Estrategia: usar cache de localStorage al instante, refrescar en background si online.
// Fallback final: tipos hardcodeados aproximados (suficientes para referencia de viaje).

import { useEffect, useState } from "react";

const STORAGE_KEY = "currency-rates-v1";
const API_URL = "https://open.er-api.com/v6/latest/USD";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Fallback aproximado (actualizado feb 2026). Base = USD.
const FALLBACK_RATES = {
  USD: 1,
  EUR: 0.92,
  HUF: 360,
  CZK: 23.2,
  ILS: 3.6,
  CLP: 950,
};

function loadCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* noop */
  }
}

export function useCurrencyRates() {
  const cached = loadCache();
  const [rates, setRates] = useState(cached?.rates || FALLBACK_RATES);
  const [updatedAt, setUpdatedAt] = useState(cached?.updatedAt || null);
  const [source, setSource] = useState(cached ? "cache" : "fallback");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Refrescar si no hay cache o si el cache es de hace >1 día
    const stale = !cached || Date.now() - (cached.updatedAt || 0) > ONE_DAY_MS;
    if (!stale) return;
    if (!navigator.onLine) return;

    setLoading(true);
    fetch(API_URL)
      .then((r) => r.json())
      .then((data) => {
        if (!data?.rates) throw new Error("no rates");
        // Quedarnos sólo con las monedas que usamos (más liviano)
        const wanted = ["USD", "EUR", "HUF", "CZK", "ILS", "CLP"];
        const slim = {};
        for (const c of wanted) {
          if (typeof data.rates[c] === "number") slim[c] = data.rates[c];
        }
        const ts = Date.now();
        setRates(slim);
        setUpdatedAt(ts);
        setSource("live");
        saveCache({ rates: slim, updatedAt: ts });
      })
      .catch(() => {
        // mantener cache/fallback
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { rates, updatedAt, source, loading };
}

// convert(amount, fromCode, toCode, rates) — rates con base USD
export function convertAmount(amount, from, to, rates) {
  if (!rates || !rates[from] || !rates[to]) return null;
  const amt = Number(amount);
  if (!Number.isFinite(amt)) return null;
  // amount(from) -> USD -> to
  const usd = amt / rates[from];
  return usd * rates[to];
}
